import { useEffect, useMemo, useState } from "react";
import { useRouterState } from "@tanstack/react-router";

const STORAGE_KEY = "utm_parameters";

type UtmEntries = ReadonlyArray<readonly [string, string]>;

export type InboundUtmParameters = ReadonlyMap<string, string>;

const isUtmKey = (key: string): boolean => key.toLowerCase().startsWith("utm");

function parseUtmEntries(search: string): UtmEntries {
  if (search.trim() === "") {
    return [];
  }

  const entries: Array<[string, string]> = [];
  const params = new URLSearchParams(search);

  params.forEach((value, key) => {
    if (!isUtmKey(key)) {
      return;
    }

    entries.push([key, value]);
  });

  return entries;
}

const createMap = (entries: UtmEntries): InboundUtmParameters =>
  new Map(entries);

function entriesEqual(
  current: InboundUtmParameters,
  nextEntries: UtmEntries,
): boolean {
  if (current.size !== nextEntries.length) {
    return false;
  }

  return nextEntries.every(([key, value]) => current.get(key) === value);
}

function persistEntries(entries: UtmEntries): void {
  if (entries.length === 0 || typeof window === "undefined") {
    return;
  }

  try {
    const serialized = JSON.stringify(Object.fromEntries(entries));
    window.sessionStorage.setItem(STORAGE_KEY, serialized);
  } catch (error) {
    console.warn("Failed to persist UTM parameters", error);
  }
}

function loadPersistedEntries(): UtmEntries {
  if (typeof window === "undefined") {
    return [];
  }

  try {
    const serialized = window.sessionStorage.getItem(STORAGE_KEY);
    if (!serialized) {
      return [];
    }

    const parsed = JSON.parse(serialized) as unknown;
    if (parsed === null || typeof parsed !== "object") {
      return [];
    }

    const objectEntries = Object.entries(parsed as Record<string, unknown>);
    const entries: Array<[string, string]> = [];

    for (const [key, value] of objectEntries) {
      if (!isUtmKey(key) || typeof value !== "string") {
        continue;
      }

      entries.push([key, value]);
    }

    return entries;
  } catch (error) {
    console.warn("Failed to restore persisted UTM parameters", error);
    return [];
  }
}

function resolveSearchString(search: unknown): string {
  if (typeof window === "undefined") {
    return typeof search === "string" ? search : "";
  }

  if (typeof search === "string") {
    return search;
  }

  return window.location.search;
}

export function useInboundUtmParameters(): InboundUtmParameters {
  const locationSearch = useRouterState({
    select: (state) => state.location.search,
  });

  const initialEntries = useMemo(() => {
    if (typeof window === "undefined") {
      return loadPersistedEntries();
    }

    const searchString = resolveSearchString(locationSearch);
    const entries = parseUtmEntries(searchString);

    if (entries.length > 0) {
      return entries;
    }

    return loadPersistedEntries();
  }, [locationSearch]);

  const [utmParameters, setUtmParameters] = useState<InboundUtmParameters>(() =>
    createMap(initialEntries),
  );

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const searchString = resolveSearchString(locationSearch);
    const entries = parseUtmEntries(searchString);

    if (entries.length > 0) {
      setUtmParameters((previous: InboundUtmParameters) => {
        if (entriesEqual(previous, entries)) {
          return previous;
        }

        persistEntries(entries);
        return createMap(entries);
      });
      return;
    }

    setUtmParameters((previous: InboundUtmParameters) => {
      if (previous.size > 0) {
        return previous;
      }

      const persistedEntries = loadPersistedEntries();
      if (persistedEntries.length === 0) {
        return previous;
      }

      return createMap(persistedEntries);
    });
  }, [locationSearch]);

  return utmParameters;
}
